import { Link } from "react-router-dom";
import {
  Sparkles,
  KeyRound,
  ArrowRight,
  Check,
  MapPin,
  ShieldCheck,
  Clock,
} from "lucide-react";
import { Reveal, Eyebrow, SectionHeader } from "../components/common";
import { CTASection, Reviews } from "../components/sections";
import { IMAGES, ZONES, PROCESS, COMPANY } from "../data/site";
import Seo from "../components/Seo";
import { SEO } from "../data/seo";

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo {...SEO.home} />
      {/* HERO */}
      <section className="relative" style={{ minHeight: "100vh" }}>
        <img src={IMAGES.hero} alt="Littoral héraultais" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative container-x flex flex-col justify-center" style={{ minHeight: "100vh" }}>
          <Reveal>
            <div
              className="text-white mb-2 flex flex-col items-end text-right"
              data-testid="hero-brand"
            >
              <svg
                viewBox="-30 -10 700 180"
                style={{
                  width: "clamp(340px, 60vw, 760px)",
                  height: "auto",
                  overflow: "visible",
                  filter: "drop-shadow(0 2px 20px rgba(0,0,0,0.4))",
                }}
              >
                <defs>
                  <clipPath id="writeClip">
                    <rect className="write-rect" x="-30" y="-10" width="0" height="180" />
                  </clipPath>
                </defs>
                <text
                  clipPath="url(#writeClip)"
                  x="640"
                  y="110"
                  textAnchor="end"
                  fontFamily="'Great Vibes', cursive"
                  fontSize="120"
                  fill="var(--gold-2)"
                >
                  Cleanova - Sud
                </text>
              </svg>
              <svg
                width="260"
                height="26"
                viewBox="0 0 260 26"
                fill="none"
                style={{ marginTop: "-6px", marginRight: "0.5rem" }}
              >
                <path
                  className="sig-underline"
                  d="M4 16 C 60 4, 120 4, 180 12 C 210 16, 235 18, 256 10"
                  stroke="var(--gold-2)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <Eyebrow light>Ménage &amp; Conciergerie haut de gamme</Eyebrow>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="display-title text-white mt-6 text-6xl md:text-8xl max-w-4xl">
              L'excellence au service<br />
              de vos <em style={{ color: "var(--gold-2)" }}>locations</em>
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-7 text-lg md:text-xl max-w-2xl" style={{ color: "rgba(255,255,255,0.85)" }}>
              {COMPANY.name} vous accompagne avec rigueur et professionnalisme
              pour sublimer vos biens et enchanter vos voyageurs.
            </p>
          </Reveal>
          <Reveal delay={340}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn btn-primary" data-testid="hero-devis">
                Demander un devis <ArrowRight size={16} />
              </Link>
              <a href="#prestations" className="btn btn-outline" data-testid="hero-services">
                Découvrir nos services
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CHOOSE SERVICE */}
      <section className="section" id="prestations" style={{ scrollMarginTop: "90px" }}>
        <div className="container-x">
          <SectionHeader
            eyebrow="Choisissez votre service"
            title="Deux métiers, une seule exigence"
            sub="Ménage professionnel et conciergerie haut de gamme pour une gestion complète et sereine de vos locations saisonnières."
          />
          <div className="grid md:grid-cols-2 gap-6 mt-14">
            <QuickCard
              icon={<Sparkles size={26} />}
              title="Ménage"
              text="Nettoyage premium & préparation des logements."
              to="/menage"
            />
            <QuickCard
              icon={<KeyRound size={26} />}
              title="Conciergerie"
              text="Gestion complète & accompagnement des voyageurs."
              to="/conciergerie"
            />
          </div>
        </div>
      </section>

      {/* SERVICES DETAIL */}
      <section className="section" style={{ background: "var(--cream)" }}>
        <div className="container-x">
          <SectionHeader eyebrow="Nos services" title="Une expertise à votre service" sub="Deux métiers complémentaires pour une gestion complète et sereine de vos locations saisonnières." />
          <div className="grid md:grid-cols-2 gap-8 mt-14">
            <DetailCard
              image={IMAGES.bedroom}
              icon={<Sparkles size={20} />}
              title="Ménage Professionnel"
              text="Un nettoyage impeccable pour des logements prêts à accueillir vos voyageurs. Propreté irréprochable, finitions soignées et gestion complète du linge."
              features={["Nettoyage complet", "Gestion du linge", "Contrôle qualité", "Réactivité 7j/7"]}
              to="/menage"
            />
            <DetailCard
              image={IMAGES.luxLiving}
              icon={<KeyRound size={20} />}
              title="Conciergerie Haut de Gamme"
              text="Un accompagnement sur-mesure pour une gestion sereine de vos locations. De l'accueil des voyageurs au suivi personnalisé de votre bien."
              features={["Accueil voyageurs", "Gestion des clés", "Préparation logement", "Suivi propriétaire"]}
              to="/conciergerie"
            />
          </div>
        </div>
      </section>

      {/* METHOD */}
      <section className="section">
        <div className="container-x">
          <SectionHeader eyebrow="Notre méthode" title="Un processus simple et efficace" sub="Du premier contact à la prestation régulière, nous vous accompagnons avec rigueur et professionnalisme." />
          <div className="grid md:grid-cols-4 gap-6 mt-14">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 100}>
                <div className="h-full">
                  <div className="font-display text-6xl" style={{ color: "var(--line)" }}>{p.step}</div>
                  <h3 className="font-display text-2xl mt-3">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--muted)" }}>{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ZONE */}
      <section className="section" style={{ background: "var(--ink)" }} data-testid="zone-section">
        <div className="container-x">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <Reveal><Eyebrow light>Zone d'intervention</Eyebrow></Reveal>
              <Reveal delay={80}>
                <h2 className="display-title text-white mt-4 text-4xl md:text-5xl">
                  Nous intervenons dans tout le <em style={{ color: "var(--gold-2)" }}>littoral héraultais</em>
                </h2>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-5" style={{ color: "rgba(255,255,255,0.7)" }}>
                  De Béziers au Cap d'Agde, en passant par Valras-Plage et Sérignan,
                  nous couvrons l'ensemble du bassin touristique de l'Hérault.
                </p>
              </Reveal>
              <div className="grid sm:grid-cols-2 gap-4 mt-9">
                {ZONES.map((z, i) => (
                  <Reveal key={z.name} delay={i * 70}>
                    <div className="zone-card p-5 flex items-start gap-3">
                      <MapPin size={20} style={{ color: "var(--gold)", marginTop: 2 }} />
                      <div>
                        <div className="text-white font-medium">{z.name}</div>
                        <div className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.55)" }}>{z.note}</div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <Reveal delay={120}>
              <div className="relative">
                <img src={IMAGES.coastNight} alt="Littoral" className="w-full object-cover" style={{ height: 480 }} />
                <div
                  className="absolute -bottom-6 -left-6 px-7 py-5 text-center"
                  style={{ background: "var(--gold)", color: "#fff" }}
                >
                  <div className="font-display text-3xl">7j/7</div>
                  <div className="text-xs tracking-widest uppercase mt-1">Disponibilité</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="py-14" style={{ background: "var(--cream)" }}>
        <div className="container-x grid sm:grid-cols-3 gap-8 text-center">
          {[
            { icon: <ShieldCheck size={26} />, t: "Assurance incluse", s: "Responsabilité civile professionnelle" },
            { icon: <Check size={26} />, t: "Contrôle qualité", s: "Check-list & photos avant/après" },
            { icon: <Clock size={26} />, t: "Réactivité 7j/7", s: "Adaptés à vos réservations" },
          ].map((x, i) => (
            <Reveal key={x.t} delay={i * 100}>
              <div className="flex flex-col items-center">
                <div style={{ color: "var(--gold)" }}>{x.icon}</div>
                <div className="font-display text-2xl mt-3">{x.t}</div>
                <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>{x.s}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Reviews />

      {/* FAQ teaser */}
      <section className="section">
        <div className="container-x text-center max-w-2xl mx-auto">
          <Reveal><Eyebrow>Une question ?</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="display-title mt-4 text-4xl md:text-5xl">Retrouvez toutes nos réponses</h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-5" style={{ color: "var(--muted)" }}>
              Toutes les réponses aux questions les plus fréquentes sur nos services de
              ménage Airbnb, conciergerie, paniers d'accueil et nos applications Cleanéo &amp; HostBook.
            </p>
          </Reveal>
          <Reveal delay={220}>
            <Link to="/a-propos/faq-generale" className="btn btn-outline-dark mt-8">
              Voir la FAQ complète
            </Link>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}

function QuickCard({ icon, title, text, to }) {
  return (
    <Link to={to} className="svc-card p-9 flex items-center justify-between group" data-testid={`quick-${title.toLowerCase()}`}>
      <div className="flex items-center gap-5">
        <div
          className="flex items-center justify-center"
          style={{ width: 64, height: 64, background: "var(--cream)", color: "var(--gold)" }}
        >
          {icon}
        </div>
        <div>
          <h3 className="font-display text-3xl">{title}</h3>
          <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>{text}</p>
        </div>
      </div>
      <ArrowRight size={22} style={{ color: "var(--gold)" }} className="transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

function DetailCard({ image, icon, title, text, features, to }) {
  return (
    <Reveal>
      <div className="svc-card h-full flex flex-col">
        <div className="overflow-hidden relative" style={{ height: 280 }}>
          <img src={image} alt={title} className="svc-img w-full h-full object-cover" />
          <div className="absolute top-5 left-5 flex items-center justify-center" style={{ width: 46, height: 46, background: "var(--gold)", color: "#fff" }}>
            {icon}
          </div>
        </div>
        <div className="p-8 flex flex-col flex-1">
          <h3 className="font-display text-3xl">{title}</h3>
          <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--muted)" }}>{text}</p>
          <ul className="grid grid-cols-2 gap-y-3 gap-x-4 mt-6">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm">
                <Check size={16} style={{ color: "var(--gold)" }} /> {f}
              </li>
            ))}
          </ul>
          <Link to={to} className="inline-flex items-center gap-2 mt-7 text-sm font-medium uppercase tracking-wider" style={{ color: "var(--gold)" }}>
            En savoir plus <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </Reveal>
  );
}
